import Confetti from "react-confetti";
import React, { useContext } from "react";
import { UserContext } from "./context/userContext";
import useMessage from "./customHook/message";

export default function Game() {
  const [dice, setDice] = React.useState(allNewDice());
  const [tenzies, setTenzies] = React.useState(false);
  const [startGame, setStartGame] = React.useState(false);
  const [startTime, setStartTime] = React.useState(0);
  const [rollNo, setRollNo] = React.useState(0);

  const { success, contextHolder } = useMessage();
  const context = useContext(UserContext);

  if (!context) {
    throw new Error("useContext must be used within a UserProvider");
  }

  const { userDetails } = context;
  const heldCount = dice.filter((die) => die.isHeld).length;

  const saveGameData = React.useCallback(async () => {
    if (!userDetails) return;

    const response = await fetch("http://localhost:5000/gameData", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        user: userDetails.name,
        userId: userDetails.id,
        time: formatTime(startTime),
        noOfRolls: rollNo,
      }),
    });

    if (response.ok) {
      success("Game data saved successfully");
    } else {
      console.log("Game data save failed");
    }
  }, [rollNo, startTime, userDetails, success]);

  React.useEffect(() => {
    const allHeld = dice.every((die) => die.isHeld);
    const firstValue = dice[0]?.value;
    const sameValue = dice.every((die) => die.value === firstValue);

    if (allHeld && sameValue && startGame) {
      setTenzies(true);
      saveGameData();
      success("You won!");
    }
  }, [dice, startGame, saveGameData, success]);

  React.useEffect(() => {
    let timer;

    if (startGame && !tenzies) {
      timer = setInterval(() => {
        setStartTime((prevTime) => prevTime + 1);
      }, 1000);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [startGame, tenzies]);

  function allNewDice() {
    return Array.from({ length: 10 }, (_, i) => ({
      value: Math.floor(Math.random() * 6) + 1,
      id: i,
      isHeld: false,
    }));
  }

  function formatTime(seconds) {
    const hours = Math.floor(seconds / 3600)
      .toString()
      .padStart(2, "0");
    const minutes = Math.floor((seconds % 3600) / 60)
      .toString()
      .padStart(2, "0");
    const secs = (seconds % 60).toString().padStart(2, "0");
    return `${hours}:${minutes}:${secs}`;
  }

  function rollDice() {
    if (!startGame) return;

    if (tenzies) {
      resetGame();
      setStartGame(true);
      return;
    }

    setDice((oldDice) =>
      oldDice.map((die) => ({
        ...die,
        value: die.isHeld ? die.value : Math.floor(Math.random() * 6) + 1,
      }))
    );
    setRollNo((prevRoll) => prevRoll + 1);
  }

  function toggleId(id) {
    setDice(
      dice.map((die) => ({
        ...die,
        isHeld: id === die.id ? !die.isHeld : die.isHeld,
      }))
    );
  }

  function resetGame() {
    setDice(allNewDice());
    setTenzies(false);
    setStartGame(false);
    setStartTime(0);
    setRollNo(0);
  }

  const randomDice = dice.map((item) => (
    <button
      key={item.id}
      onClick={() => toggleId(item.id)}
      className={`group relative flex h-24 w-24 items-center justify-center rounded-3xl border transition-all duration-200 shadow-lg focus:outline-none focus:ring-4 focus:ring-sky-300 ${
        item.isHeld
          ? "border-sky-800 bg-sky-900 text-white"
          : "border-slate-200 bg-white text-slate-900 hover:-translate-y-1"
      }`}
      aria-pressed={item.isHeld}
    >
      <span className="text-4xl font-semibold">{item.value}</span>
      <span className="absolute bottom-3 text-xs uppercase tracking-[0.15em] text-slate-200 opacity-80">
        {item.isHeld ? "Held" : "Tap to hold"}
      </span>
    </button>
  ));

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-500 via-cyan-400 to-indigo-700 px-4 py-10 text-slate-900">
      {contextHolder}
      <div className="mx-auto w-full max-w-6xl rounded-[2rem] bg-white/95 p-6 shadow-2xl shadow-slate-900/20 backdrop-blur-xl ring-1 ring-slate-200/50">
        {tenzies && <Confetti recycle={false} />}

        <div className="flex flex-col items-center gap-3 text-center">
          <div className="rounded-full bg-slate-900 px-4 py-2 text-sm font-medium uppercase tracking-[0.25em] text-white shadow-lg shadow-slate-900/20">
            Tenzies Challenge
          </div>
          <h1 className="text-4xl font-extrabold sm:text-5xl">Play Tenzies</h1>
          <p className="max-w-3xl text-slate-600 sm:text-lg">
            Roll the dice until all 10 values match. Tap a die to lock it in place between rolls, then keep rolling to finish the round.
          </p>
        </div>

        <div className="mt-10 grid gap-8 lg:grid-cols-[1.8fr_1fr]">
          <div className="rounded-[2rem] border border-slate-200 bg-slate-950/95 p-6 text-slate-100 shadow-xl shadow-slate-900/10">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm uppercase tracking-[0.2em] text-sky-300">Game board</p>
                <h2 className="text-2xl font-semibold">Dice grid</h2>
              </div>
              <div className="rounded-full bg-slate-800/90 px-4 py-2 text-sm uppercase tracking-[0.2em] text-slate-200">
                {heldCount} held
              </div>
            </div>

            <div className="grid grid-cols-5 gap-4 sm:grid-cols-5"> 
              {randomDice}
            </div>

            {!startGame && (
              <div className="mt-6 rounded-3xl bg-slate-900/80 p-6 text-slate-100">
                <p className="text-base leading-7">
                  Press <strong>Play now</strong> to begin your first round. Each die can be held before rolling the remaining dice.
                </p>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-xl shadow-slate-900/10">
              <h2 className="text-xl font-semibold text-slate-900">Quick stats</h2>
              <div className="mt-4 space-y-4 text-slate-700">
                <div className="flex justify-between rounded-2xl bg-slate-50 p-4">
                  <span className="font-medium">Status</span>
                  <span className="font-semibold text-slate-900">{tenzies ? "Completed" : startGame ? "In progress" : "Not started"}</span>
                </div>
                <div className="flex justify-between rounded-2xl bg-slate-50 p-4">
                  <span className="font-medium">Time</span>
                  <span className="font-semibold text-slate-900">{formatTime(startTime)}</span>
                </div>
                <div className="flex justify-between rounded-2xl bg-slate-50 p-4">
                  <span className="font-medium">Rolls</span>
                  <span className="font-semibold text-slate-900">{rollNo}</span>
                </div>
                <div className="flex justify-between rounded-2xl bg-slate-50 p-4">
                  <span className="font-medium">Player</span>
                  <span className="font-semibold text-slate-900">{userDetails?.name || "Guest"}</span>
                </div>
              </div>
            </div>

            <div className="rounded-[2rem] border border-slate-200 bg-slate-50 p-6 shadow-xl shadow-slate-900/5">
              <h2 className="text-xl font-semibold text-slate-900">Controls</h2>
              <p className="mt-3 text-sm leading-6 text-slate-600">
                Use the buttons below to start, roll, or reset your round. When you win, a new game is ready with a single click.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                {!startGame ? (
                  <button
                    onClick={() => {
                      setStartGame(true);
                      setStartTime(0);
                      setRollNo(0);
                      setDice(allNewDice());
                    }}
                    className="inline-flex min-w-[140px] items-center justify-center rounded-full bg-sky-600 px-6 py-3 text-sm font-semibold text-white transition hover:bg-sky-500 focus:outline-none focus:ring-4 focus:ring-sky-300"
                  >
                    Play now
                  </button>
                ) : (
                  <button
                    onClick={rollDice}
                    className="inline-flex min-w-[140px] items-center justify-center rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 focus:outline-none focus:ring-4 focus:ring-slate-300"
                  >
                    {tenzies ? "Start New Game" : "Roll dice"}
                  </button>
                )}

                {startGame && !tenzies && (
                  <button
                    onClick={resetGame}
                    className="inline-flex min-w-[140px] items-center justify-center rounded-full border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-800 transition hover:bg-slate-100 focus:outline-none focus:ring-4 focus:ring-slate-200"
                  >
                    Reset round
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
